import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import NotificationBell from '../notifications/NotificationBell'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handle)
    return () => window.removeEventListener('resize', handle)
  }, [])
  return isMobile
}

const NAV_ITEMS = [
  { path: '/dashboard', label: 'DASHBOARD', icon: String.fromCodePoint(0x2B1B), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader', 'laborer'] },
  { path: '/leaderboard', label: 'LEADERBOARD', icon: String.fromCodePoint(0x1F3C6), roles: ['laborer'] },
  { path: '/hours', label: 'MY HOURS', icon: String.fromCodePoint(0x23F1), roles: ['laborer'] },
  { path: '/laborer-settings', label: 'SETTINGS', icon: String.fromCodePoint(0x2699), roles: ['laborer'] },
  { path: '/calendar', label: 'CALENDAR', icon: String.fromCodePoint(0x1F4C5), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
  { path: '/jobs', label: 'JOBS', icon: String.fromCodePoint(0x1F3D7), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
  { path: '/pool-leads', label: 'POOL LEADS', icon: String.fromCodePoint(0x1F3CA), roles: ['owner', 'office_staff'] },
  { path: '/opportunities', label: 'OPPORTUNITIES', icon: String.fromCodePoint(0x1F3AF), roles: ['owner', 'office_staff', 'project_manager'] },
  { path: '/bids', label: 'BIDS', icon: String.fromCodePoint(0x1F4CB), roles: ['owner', 'office_staff', 'project_manager'] },
  { path: '/emails', label: 'EMAILS', icon: String.fromCodePoint(0x1F4E7), roles: ['owner', 'office_staff', 'project_manager'] },
  { path: '/invoicing', label: 'INVOICES', icon: String.fromCodePoint(0x1F4B0), roles: ['owner', 'office_staff'] },
  { path: '/store', label: 'STORE PORTAL', icon: String.fromCodePoint(0x1F3EA), roles: ['store_partner'] },
  { path: '/store-requests', label: 'STORE REQUESTS', icon: String.fromCodePoint(0x1F4E5), roles: ['owner', 'office_staff'] },
  { path: '/store-questions', label: 'STORE QUESTIONS', icon: String.fromCodePoint(0x1F4DE), roles: ['owner', 'office_staff'] },
  { path: '/relations', label: 'RELATIONS', icon: String.fromCodePoint(0x1F91D), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
  { path: '/equipment', label: 'EQUIPMENT', icon: String.fromCodePoint(0x1F69C), roles: ['owner', 'office_staff', 'project_manager'] },
  { path: '/expenses', label: 'EXPENSES', icon: String.fromCodePoint(0x1F4B3), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
  { path: '/timeclock', label: 'TIME CLOCK', icon: String.fromCodePoint(0x23F1), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
  { path: '/chat', label: 'MESSAGES', icon: String.fromCodePoint(0x1F4AC), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader', 'laborer'] },
  { path: '/documents', label: 'DOCUMENTS', icon: String.fromCodePoint(0x1F4C4), roles: ['owner', 'office_staff', 'crew_leader'] },
  { path: '/cashflow', label: 'CASH FLOW', icon: String.fromCodePoint(0x1F4C8), roles: ['owner', 'office_staff', 'project_manager'] },
  { path: '/salesperson-reports', label: 'SALES REPORTS', icon: String.fromCodePoint(0x1F4CA), roles: ['owner', 'office_staff'] },
  { path: '/insights', label: 'INSIGHTS', icon: String.fromCodePoint(0x1F4C8), roles: ['owner', 'office_staff'] },
  { path: '/myday', label: 'MY DAY', icon: String.fromCodePoint(0x2600), roles: ['crew_leader', 'project_manager'] },
  { path: '/schedule', label: 'SCHEDULE', icon: String.fromCodePoint(0x1F5D3), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
  { path: '/schedule/board', label: 'CREW BOARD', icon: String.fromCodePoint(0x1F4CC), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
  { path: '/team', label: 'TEAM', icon: String.fromCodePoint(0x1F465), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
  { path: '/pricing', label: 'PRICING', icon: String.fromCodePoint(0x1F4B2), roles: ['owner', 'office_staff'] },
  { path: '/time-standards', label: 'TIME STANDARDS', icon: String.fromCodePoint(0x23F1), roles: ['owner'] },
  { path: '/leads', label: 'LEAD GEN', icon: String.fromCodePoint(0x1F3AF), roles: ['owner', 'office_staff'] },
  { path: '/renders', label: 'RENDERS', icon: String.fromCodePoint(0x1F5BC), roles: ['owner', 'office_staff'] },
  { path: '/settings', label: 'SETTINGS', icon: String.fromCodePoint(0x2699), roles: ['owner', 'office_staff'] },
  { path: '/profile', label: 'MY PROFILE', icon: String.fromCodePoint(0x1F464), roles: ['owner', 'office_staff', 'project_manager', 'crew_leader'] },
]

const BOTTOM_NAV_DEFAULT = [
  { path: '/dashboard', label: 'Home', icon: String.fromCodePoint(0x2B1B) },
  { path: '/calendar', label: 'Calendar', icon: String.fromCodePoint(0x1F4C5) },
  { path: '/jobs', label: 'Jobs', icon: String.fromCodePoint(0x1F3D7) },
  { path: '/chat', label: 'Chat', icon: String.fromCodePoint(0x1F4AC) },
  { path: '/timeclock', label: 'Clock', icon: String.fromCodePoint(0x23F1) },
]

const BOTTOM_NAV_LABORER = [
  { path: '/dashboard', label: 'Today', icon: String.fromCodePoint(0x2B1B) },
  { path: '/leaderboard', label: 'Rankings', icon: String.fromCodePoint(0x1F3C6) },
  { path: '/hours', label: 'Hours', icon: String.fromCodePoint(0x23F1) },
  { path: '/laborer-settings', label: 'Settings', icon: String.fromCodePoint(0x2699) },
]

const BOTTOM_NAV_CREW_LEADER = [
  { path: '/dashboard', label: 'Today', icon: String.fromCodePoint(0x2B1B) },
  { path: '/jobs', label: 'Jobs', icon: String.fromCodePoint(0x1F3D7) },
  { path: '/leaderboard', label: 'Rankings', icon: String.fromCodePoint(0x1F3C6) },
  { path: '/schedule', label: 'Schedule', icon: String.fromCodePoint(0x1F5D3) },
  { path: '/chat', label: 'Chat', icon: String.fromCodePoint(0x1F4AC) },
]

const BOTTOM_NAV_STORE = [
  { path: '/store', label: 'Store', icon: String.fromCodePoint(0x1F3EA) },
  { path: '/chat', label: 'Chat', icon: String.fromCodePoint(0x1F4AC) },
]

function bottomNavForRole(role) {
  if (role === 'laborer') return BOTTOM_NAV_LABORER
  if (role === 'crew_leader' || role === 'project_manager') return BOTTOM_NAV_CREW_LEADER
  if (role === 'store_partner') return BOTTOM_NAV_STORE
  return BOTTOM_NAV_DEFAULT
}

export default function AppLayout({ children }) {
  const { user, profile, signOut, activeBusiness, setActiveBusiness } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const role = profile?.role
  const showBell = role === 'owner' || role === 'office_staff' || role === 'store_partner' || role === 'laborer' || role === 'crew_leader'
  const visibleNav = NAV_ITEMS.filter(item => item.roles.includes(role))

  // Persistent clock-in banner — fetches from DB independently
  const [clockedInData, setClockedInData] = useState(null)
  const [bannerElapsed, setBannerElapsed] = useState('')
  const [bannerClockingOut, setBannerClockingOut] = useState(false)
  const bannerIntervalRef = useRef(null)
  const showClockBanner = ['crew_leader', 'laborer', 'project_manager'].includes(role)

  const fetchClockStatus = useCallback(async function() {
    if (!user?.id || !showClockBanner) return
    // Find open session regardless of work_date — survives UTC/ET rollover.
    var { data } = await supabase.from('daily_attendance').select('*')
      .eq('user_id', user.id).eq('status', 'clocked_in').is('clock_out', null)
      .order('clock_in', { ascending: false }).limit(1).maybeSingle()
    setClockedInData(data || null)
    if (data && data.clock_in) {
      var ms = Date.now() - new Date(data.clock_in).getTime()
      var h = Math.floor(ms / 3600000); var m = Math.floor((ms % 3600000) / 60000)
      setBannerElapsed(h + 'h ' + m + 'm')
    }
  }, [user?.id, showClockBanner])

  useEffect(function() { fetchClockStatus() }, [fetchClockStatus, location.pathname])

  useEffect(function() {
    if (bannerIntervalRef.current) clearInterval(bannerIntervalRef.current)
    if (clockedInData && clockedInData.clock_in) {
      bannerIntervalRef.current = setInterval(function() {
        var ms = Date.now() - new Date(clockedInData.clock_in).getTime()
        var h = Math.floor(ms / 3600000); var m = Math.floor((ms % 3600000) / 60000)
        setBannerElapsed(h + 'h ' + m + 'm')
      }, 30000)
    }
    return function() { if (bannerIntervalRef.current) clearInterval(bannerIntervalRef.current) }
  }, [clockedInData])

  var handleBannerClockOut = async function() {
    if (!clockedInData || bannerClockingOut) return
    setBannerClockingOut(true)
    var now = new Date()
    await supabase.from('daily_attendance').update({
      clock_out: now.toISOString(), status: 'clocked_out'
    }).eq('id', clockedInData.id)
    setClockedInData(null)
    setBannerClockingOut(false)
  }

  const businessLabel = activeBusiness === 'all' ? 'ALL BUSINESSES'
    : activeBusiness === 'pools' ? 'CP QUALITY POOLS'
    : 'RIVERBEND CLEARING'

  return (
    <div style={styles.root}>
      {!isMobile && (
        <aside style={styles.sidebar}>
          <div style={styles.sidebarTop}>
            <img src="/cp-quality-logo.png" alt="CP Quality Pools" style={styles.logoMark} />
            <div>
              <div style={styles.logoName}>PIETILA</div>
              <div style={styles.logoSub}>GROUP</div>
            </div>
          </div>
          {role !== 'store_partner' && (
            <div style={styles.bizSwitcher}>
              {['all', 'pools', 'riverbend'].map(biz => (
                <button key={biz} onClick={() => setActiveBusiness(biz)} style={{ ...styles.bizBtn, ...(activeBusiness === biz ? styles.bizBtnActive : {}) }}>
                  {biz === 'all' ? 'ALL' : biz === 'pools' ? 'CPP' : 'RBC'}
                </button>
              ))}
            </div>
          )}
          <div style={styles.bizLabel}>{businessLabel}</div>
          <nav style={styles.nav}>
            {visibleNav.map(item => (
              <button key={item.path} onClick={() => navigate(item.path)} style={{ ...styles.navItem, ...(location.pathname === item.path ? styles.navItemActive : {}) }}>
                <span style={styles.navIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
          <div style={styles.sidebarBottom}>
            <div style={styles.userInfo}>
              <div style={styles.userName}>{profile?.full_name || 'User'}</div>
              <div style={styles.userRole}>{role?.replace('_', ' ').toUpperCase()}</div>
            </div>
            <button onClick={signOut} style={styles.signOutBtn}>OUT</button>
          </div>
        </aside>
      )}

      <main style={{ ...styles.main, marginLeft: isMobile ? 0 : '220px' }}>
        <div style={styles.topBar}>
          <img src="/cp-quality-logo.png" alt="CP Quality Pools" style={styles.topLogo} />
          <div style={styles.topBizLabel}>{businessLabel}</div>
          {showBell && <NotificationBell />}
          {role !== 'store_partner' && (
            <div style={styles.topBizButtons}>
              {['all', 'pools', 'riverbend'].map(biz => (
                <button key={biz} onClick={() => setActiveBusiness(biz)} style={{ ...styles.topBizBtn, ...(activeBusiness === biz ? styles.topBizBtnActive : {}) }}>
                  {biz === 'all' ? 'ALL' : biz === 'pools' ? 'CPP' : 'RBC'}
                </button>
              ))}
            </div>
          )}
        </div>
        {clockedInData && showClockBanner && (
          <div style={styles.clockBanner}>
            <div style={styles.clockBannerText}>
              <span style={styles.clockBannerDot} />
              Clocked in since {new Date(clockedInData.clock_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} — {bannerElapsed}
            </div>
            <button onClick={handleBannerClockOut} disabled={bannerClockingOut} style={styles.clockBannerBtn}>
              {bannerClockingOut ? '...' : 'CLOCK OUT'}
            </button>
          </div>
        )}
        <div style={{ ...styles.content, paddingBottom: isMobile ? '90px' : '32px' }}>{children}</div>
      </main>

      {isMobile && (
        <nav style={styles.bottomNav}>
          {bottomNavForRole(role).map(item => (
            <button key={item.path} onClick={() => navigate(item.path)} style={{ ...styles.bottomNavItem, ...(location.pathname === item.path ? styles.bottomNavItemActive : {}) }}>
              <span style={styles.bottomNavIcon}>{item.icon}</span>
              <span style={styles.bottomNavLabel}>{item.label}</span>
            </button>
          ))}
        </nav>
      )}
    </div>
  )
}

const styles = {
  root: { display: 'flex', minHeight: '100vh', backgroundColor: '#0a0f1a', fontFamily: "'Barlow', sans-serif", color: '#ffffff' },
  sidebar: { width: '220px', flexShrink: 0, backgroundColor: '#0d1420', borderRight: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', padding: '24px 0', position: 'fixed', top: 0, left: 0, height: '100vh', overflowY: 'auto', zIndex: 100 },
  sidebarTop: { display: 'flex', alignItems: 'center', gap: '12px', padding: '0 20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '20px' },
  logoMark: { width: '40px', height: '40px', objectFit: 'contain', flexShrink: 0 },
  logoName: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '18px', letterSpacing: '3px', color: '#ffffff', lineHeight: 1 },
  logoSub: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '3px', color: '#b48c3c', marginTop: '2px' },
  bizSwitcher: { display: 'flex', gap: '4px', padding: '0 20px 8px' },
  bizBtn: { flex: 1, padding: '6px 4px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  bizBtnActive: { backgroundColor: '#b48c3c', borderColor: '#b48c3c', color: '#0a0f1a' },
  bizLabel: { padding: '0 20px 16px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.25)' },
  nav: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 12px' },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', backgroundColor: 'transparent', border: 'none', borderRadius: '3px', color: 'rgba(255,255,255,0.45)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all 0.15s' },
  navItemActive: { backgroundColor: 'rgba(180,140,60,0.12)', color: '#b48c3c', borderLeft: '2px solid #b48c3c' },
  navIcon: { fontSize: '14px', width: '18px', textAlign: 'center' },
  sidebarBottom: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '16px' },
  userInfo: {},
  userName: { fontSize: '14px', fontWeight: 500, color: '#ffffff' },
  userRole: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: '#b48c3c', marginTop: '2px' },
  signOutBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '6px 10px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1px', cursor: 'pointer' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  topBar: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', backgroundColor: '#0d1420', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 50 },
  topLogo: { width: '32px', height: '32px', objectFit: 'contain', flexShrink: 0 },
  topBizLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', flex: 1 },
  topBizButtons: { display: 'flex', gap: '4px' },
  topBizBtn: { padding: '5px 8px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  topBizBtnActive: { backgroundColor: '#b48c3c', borderColor: '#b48c3c', color: '#0a0f1a' },
  content: { flex: 1, padding: '32px' },
  bottomNav: { display: 'flex', position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#0d1420', borderTop: '1px solid rgba(255,255,255,0.08)', zIndex: 200, paddingBottom: 'env(safe-area-inset-bottom)' },
  bottomNavItem: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '10px 4px', backgroundColor: 'transparent', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer' },
  bottomNavItemActive: { color: '#b48c3c' },
  bottomNavIcon: { fontSize: '18px' },
  bottomNavLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '0.5px' },
  clockBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px', backgroundColor: 'rgba(74,140,106,0.12)', borderBottom: '1px solid rgba(74,140,106,0.25)' },
  clockBannerText: { display: 'flex', alignItems: 'center', gap: '8px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1px', color: '#4a8c6a' },
  clockBannerDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#4a8c6a', flexShrink: 0 },
  clockBannerBtn: { padding: '4px 12px', backgroundColor: '#e05c5c', border: 'none', borderRadius: '3px', color: '#ffffff', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
}
